/**
 * GiverStatement — everything one person has promised and paid.
 *
 * Read from the same canonical pledge records as the assembly's book and the
 * campaign's totals, across every assembly and every appeal, so the three can
 * never disagree. Two doors into it: the office (which sees only the assemblies
 * it already has reach over) and the giver themselves.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer, Receipt as ReceiptIcon, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import GivingLetterDialog from './GivingLetterDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Names, references and purposes are typed by people. The print window shares
 *  this origin, so every interpolated value is escaped before it is written. */
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

const printStatement = (d) => {
  const win = window.open('', '_blank', 'width=640,height=800');
  if (!win) return;
  const rows = (d.pledges || []).map((p) => `
    <tr>
      <td>${esc(p.pledged_on || '')}</td>
      <td>${esc(p.campaign_title || p.purpose || '—')}<br/><span class="muted">${esc(p.assembly || '')}</span></td>
      <td style="text-align:right">${esc(money(p.amount, p.currency))}</td>
      <td style="text-align:right">${esc(money(p.paid, p.currency))}</td>
      <td style="text-align:right">${esc(money(p.outstanding, p.currency))}</td>
    </tr>`).join('');
  const totals = Object.entries(d.by_currency || {}).map(([ccy, t]) => `
    <tr><td colspan="2"><strong>Total ${esc(ccy)}</strong></td>
    <td style="text-align:right"><strong>${esc(money(t.pledged, ccy))}</strong></td>
    <td style="text-align:right"><strong>${esc(money(t.paid, ccy))}</strong></td>
    <td style="text-align:right"><strong>${esc(money(t.outstanding, ccy))}</strong></td></tr>`).join('');
  win.document.write(`<!doctype html><html><head><title>Statement — ${esc(d.giver.name)}</title>
    <style>
      body{font-family:Georgia,serif;color:#0f172a;margin:32px}
      h1{font-size:19px;margin:0 0 2px}
      .muted{color:#64748b;font-size:11px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:18px}
      th{text-align:left;border-bottom:1px solid #cbd5e1;padding:6px 4px;font-size:11px;
         text-transform:uppercase;letter-spacing:.05em;color:#64748b}
      td{padding:6px 4px;border-bottom:1px solid #eef2f6;vertical-align:top}
    </style></head><body>
    <h1>Giving statement</h1>
    <p class="muted">${esc(d.giver.name)}${d.giver.email ? ` · ${esc(d.giver.email)}` : ''}</p>
    <table><thead><tr><th>Date</th><th>Towards</th><th style="text-align:right">Pledged</th>
      <th style="text-align:right">Paid</th><th style="text-align:right">Outstanding</th></tr></thead>
      <tbody>${rows}${totals}</tbody></table>
    <p class="muted" style="margin-top:20px">${esc(d.note || '')}</p>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
};

export const GiverStatement = ({ churchId, memberId, mine = false }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['giver-statement', mine ? 'me' : `${churchId}:${memberId}`],
    queryFn: async () => {
      if (mine) {
        const { getToken } = await import('@/lib/authToken');
        const r = await fetch(`${API_URL}/api/member/giving/pledge-statement`,
                              { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) throw new Error('unavailable');
        return r.json();
      }
      const r = await api.get(`/church/${churchId}/members/${memberId}/statement`);
      return r.data;
    },
    enabled: mine || !!(churchId && memberId),
    retry: false,
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Gathering the statement…
      </p>
    );
  }
  if (error || !data) {
    return (
      <p className="p-3 text-sm text-rose-600" data-testid="statement-error">
        This statement could not be loaded. Please try again.
      </p>
    );
  }

  const pledges = data.pledges || [];

  return (
    <div className="space-y-4" data-testid="giver-statement">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{data.giver.name || 'Giver'}</p>
          <p className="text-xs text-slate-500">
            {pledges.length} pledge{pledges.length === 1 ? '' : 's'} · {data.payments} payment
            {data.payments === 1 ? '' : 's'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => printStatement(data)}
                data-testid="statement-print">
          <Printer className="mr-1.5 h-4 w-4" />Print
        </Button>
      </div>

      {Object.keys(data.by_currency || {}).length ? (
        <div className="grid gap-2 sm:grid-cols-3" data-testid="statement-totals">
          {Object.entries(data.by_currency).map(([ccy, t]) => (
            <div key={ccy} className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{ccy}</p>
              <p className="text-sm">Pledged <strong>{money(t.pledged, ccy)}</strong></p>
              <p className="text-sm">Paid <strong>{money(t.paid, ccy)}</strong></p>
              <p className="text-sm">Outstanding <strong>{money(t.outstanding, ccy)}</strong></p>
            </div>
          ))}
        </div>
      ) : null}

      {pledges.length ? (
        <ul className="space-y-2">
          {pledges.map((p) => (
            <li key={p.id} className="rounded-lg border border-slate-200 p-3"
                data-testid={`statement-pledge-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-[10rem] flex-1 text-sm font-medium text-slate-900">
                  {p.campaign_title || p.purpose || 'General'}
                  <span className="block text-xs font-normal text-slate-500">
                    {p.assembly}{p.pledged_on ? ` · ${p.pledged_on}` : ''}
                  </span>
                </span>
                <span className="text-right text-sm">
                  {money(p.paid, p.currency)} of {money(p.amount, p.currency)}
                  <span className="block text-xs text-slate-500">
                    {money(p.outstanding, p.currency)} outstanding
                  </span>
                </span>
                <Badge variant="outline">{p.status}</Badge>
              </div>
              {p.payments?.length ? (
                <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
                  {p.payments.map((pay) => (
                    <li key={pay.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <ReceiptIcon className="h-3 w-3 text-slate-400" />
                      <span className={pay.reversed ? 'line-through' : ''}>
                        {money(pay.amount, pay.currency)}
                      </span>
                      <span className="text-slate-400">{pay.date} · {pay.method}</span>
                      {pay.receipt_number ? (
                        <span className="font-mono text-slate-500">{pay.receipt_number}</span>
                      ) : null}
                      {pay.reversed ? <Badge variant="outline">reversed</Badge> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="p-3 text-sm text-slate-500" data-testid="statement-empty">
          No pledges recorded for this giver yet.
        </p>
      )}

      {data.note ? <p className="text-xs text-slate-400">{data.note}</p> : null}
    </div>
  );
};

export const GiverStatementDialog = ({ churchId, memberId, name, onClose }) => {
  const [letterOpen, setLetterOpen] = React.useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Giving statement · {name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          <GiverStatement churchId={churchId} memberId={memberId} />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => setLetterOpen(true)}
                  data-testid="statement-letter-btn">
            <FileText className="mr-1.5 h-4 w-4" />Year-end letter
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        {letterOpen ? (
          <GivingLetterDialog churchId={churchId} memberId={memberId} name={name}
                              onClose={() => setLetterOpen(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default GiverStatement;
